import {
  ConnectionQuality,
  LocalParticipant,
  Room,
  RoomEvent,
  RoomOptions,
  VideoPresets,
} from "livekit-client";
import {
  RecordingServiceOptions,
  RecordingService,
} from "../common/hooks/useRecorder";
import { livekitWSUrl } from "../config/constants";
import { handleError } from "../helpers/sentry";
import { detectIsPortrait } from "../utils/orientationUtils";

export class LivekitService implements RecordingService {
  private options: RecordingServiceOptions = {};
  public livekitRoom;
  public videoTrack = null;
  private orientationUnsubscribe: (() => void) | null = null;

  async connect(options: RecordingServiceOptions) {
    this.options = options;
    try {
      const initialResolution = this.getResolutionForOrientation();
      
      const roomOpts: RoomOptions = {
        publishDefaults: {
          videoCodec: "vp8",
          backupCodec: {
            codec: "h264",
          },
          videoSimulcastLayers: [VideoPresets.h360, VideoPresets.h540],
        },
        videoCaptureDefaults: {
          resolution: initialResolution,
        },
      };

      const room = new Room(roomOpts);
      await room.connect(livekitWSUrl, options.streamingToken, {
        autoSubscribe: false,
      });

      await room.localParticipant.setCameraEnabled(true);
      await room.localParticipant.setMicrophoneEnabled(false);

      // Listen for orientation changes and update resolution
      this.setupOrientationListener(room);

      room.on(RoomEvent.Disconnected, (error) => {
        handleError(error);
        this.options.onConnectionFailure();
      });
      
      room.on(RoomEvent.ConnectionQualityChanged, () => {
        const isGoodQuality = this.handleNetworkQualityChange(room);
        options.onNetworkQualityChange(isGoodQuality);
      });

      this.options.onConnected();
      this.livekitRoom = room;
      console.log("Livekit connected");
    } catch (error) {
      handleError(error);
      options.onConnectionFailure(error);
    }
  }

  private setupOrientationListener(room: Room) {
    // Clean up any prior listener so reconnect doesn't leak the old one
    if (this.orientationUnsubscribe) {
      this.orientationUnsubscribe();
      this.orientationUnsubscribe = null;
    }

    let orientationChangeTimer: NodeJS.Timeout | null = null;
    let isRestarting = false;

    const handleOrientationChange = () => {
      if (orientationChangeTimer) clearTimeout(orientationChangeTimer);

      orientationChangeTimer = setTimeout(async () => {
        // If disconnect() ran while this was in flight, bail out
        if (this.livekitRoom !== room) return;
        // Mutex: skip if a restart is already in progress
        if (isRestarting) return;

        isRestarting = true;
        try {
          const newResolution = this.getResolutionForOrientation();
          await room.localParticipant.setCameraEnabled(false);
          if (this.livekitRoom !== room) return;
          await room.localParticipant.setCameraEnabled(true, { resolution: newResolution });
        } catch (error) {
          handleError(error);
          // Re-enable camera so it doesn't stay dark after a restart failure
          if (this.livekitRoom === room) {
            try { await room.localParticipant.setCameraEnabled(true); } catch { /* ignore */ }
          }
        } finally {
          isRestarting = false;
        }
      }, 300);
    };

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener("change", handleOrientationChange);
      this.orientationUnsubscribe = () => {
        window.screen.orientation?.removeEventListener("change", handleOrientationChange);
        if (orientationChangeTimer) clearTimeout(orientationChangeTimer);
      };
    } else if ("orientationchange" in window) {
      window.addEventListener("orientationchange", handleOrientationChange);
      this.orientationUnsubscribe = () => {
        window.removeEventListener("orientationchange", handleOrientationChange);
        if (orientationChangeTimer) clearTimeout(orientationChangeTimer);
      };
    } else {
      window.addEventListener("resize", handleOrientationChange);
      this.orientationUnsubscribe = () => {
        window.removeEventListener("resize", handleOrientationChange);
        if (orientationChangeTimer) clearTimeout(orientationChangeTimer);
      };
    }
  }

  private getResolutionForOrientation() {
    if (detectIsPortrait()) {
      return { width: 540, height: 960 };
    }
    return VideoPresets.h540.resolution;
  }

  previewElement(videoRef) {
    const localParticipant: LocalParticipant =
      this.livekitRoom.localParticipant;
    const localVideoTrack = Array.from(localParticipant.videoTrackPublications.values())[0]
      .track;
    if (videoRef.current && localVideoTrack) {
      this.videoTrack = localVideoTrack;
      this.videoTrack.attach(videoRef.current);
      this.videoTrack.on("stopped", this.handleTrackStopped);
    }
  }

  handleNetworkQualityChange = (room) => {
    return (
      room.localParticipant.connectionQuality === ConnectionQuality.Good ||
      room.localParticipant.connectionQuality === ConnectionQuality.Excellent
    );
  };

  disconnect(): void {
    // Clean up orientation listener
    if (this.orientationUnsubscribe) {
      this.orientationUnsubscribe();
      this.orientationUnsubscribe = null;
    }

    if (this.livekitRoom) {
      this.livekitRoom?.disconnect();
      this.livekitRoom = null;
      if (this.videoTrack) {
        this.videoTrack?.stop();
      }
    }
  }

  private handleTrackStopped = () => {
    if (this.livekitRoom) {
      this.livekitRoom.disconnect();
      this.options.onTrackChange();
    }
  };
}
